import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { resolveSupportAssetUrl } from '../../../utils/supportAssets';

/* ---- Slate JSON → React (read-only, no slate dependency) ---- */

const slugifyHeading = (text) =>
    text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');

const plainText = (node) => {
    if (typeof node?.text === 'string') return node.text;
    return (node?.children || []).map(plainText).join('');
};

const Leaf = ({ leaf }) => {
    let el = leaf.text;
    if (leaf.code) el = <code className="rs-help-inline-code">{el}</code>;
    if (leaf.bold) el = <strong>{el}</strong>;
    if (leaf.italic) el = <em>{el}</em>;
    if (leaf.underline) el = <u>{el}</u>;
    return <>{el}</>;
};

const Children = ({ nodes }) => (
    <>
        {(nodes || []).map((child, i) =>
            child.type ? <Node key={i} node={child} /> : <Leaf key={i} leaf={child} />
        )}
    </>
);

const Node = ({ node }) => {
    switch (node.type) {
        case 'heading-one':
            return <h2 id={slugifyHeading(plainText(node))}><Children nodes={node.children} /></h2>;
        case 'heading-two':
            return <h3 id={slugifyHeading(plainText(node))}><Children nodes={node.children} /></h3>;
        case 'block-quote':
            return <blockquote><Children nodes={node.children} /></blockquote>;
        case 'bulleted-list':
            return <ul><Children nodes={node.children} /></ul>;
        case 'numbered-list':
            return <ol><Children nodes={node.children} /></ol>;
        case 'list-item':
            return <li><Children nodes={node.children} /></li>;
        case 'code':
            return <pre><code>{plainText(node)}</code></pre>;
        case 'image':
            return (
                <figure>
                    <img src={resolveSupportAssetUrl(node.url)} alt={node.alt || 'Help article visual'} loading="lazy" />
                </figure>
            );
        default:
            return <p><Children nodes={node.children} /></p>;
    }
};

const formatDate = (value) => {
    if (!value) return '';
    try {
        return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
        return '';
    }
};

const HelpArticle = () => {
    const { articleSlug } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [categories, setCategories] = useState([]);
    const [status, setStatus] = useState('loading'); // loading | ready | notfound | error
    const [feedback, setFeedback] = useState('');

    useEffect(() => {
        let active = true;
        setStatus('loading');
        setFeedback('');
        axios
            .get(`/api/support/articles/${articleSlug}`)
            .then((r) => {
                if (!active) return;
                setArticle(r.data);
                setStatus('ready');
            })
            .catch((err) => {
                if (!active) return;
                setStatus(err?.response?.status === 404 ? 'notfound' : 'error');
            });
        return () => {
            active = false;
        };
    }, [articleSlug]);

    useEffect(() => {
        axios
            .get('/api/support/categories')
            .then((r) => setCategories(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [articleSlug]);

    const category = useMemo(() => {
        if (!article?.category) return null;
        return categories.find((c) => c.slug === article.category.slug) || null;
    }, [categories, article]);

    const siblings = useMemo(() => (category?.articles || []), [category]);
    const currentIndex = siblings.findIndex((a) => a.slug === articleSlug);
    const prev = currentIndex > 0 ? siblings[currentIndex - 1] : null;
    const next = currentIndex >= 0 && currentIndex < siblings.length - 1 ? siblings[currentIndex + 1] : null;

    const toc = useMemo(() => {
        if (!article?.content) return [];
        return article.content
            .filter((n) => n.type === 'heading-one' || n.type === 'heading-two')
            .map((n) => ({ text: plainText(n), id: slugifyHeading(plainText(n)) }))
            .filter((h) => h.text);
    }, [article]);

    if (status === 'loading') {
        return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading article…</div>;
    }

    if (status !== 'ready') {
        return (
            <>
                <div className="rs-page-title-row">
                    <h1 className="rs-page-title">Help center</h1>
                </div>
                <div className="rs-card" style={{ padding: 40, textAlign: 'center' }}>
                    <p style={{ color: 'var(--ink-2)', fontWeight: 600, marginBottom: 6 }}>
                        {status === 'notfound' ? 'We couldn’t find that article.' : 'Something went wrong loading this article.'}
                    </p>
                    <p style={{ color: 'var(--ink-3)', fontSize: 13, marginBottom: 18 }}>
                        {status === 'notfound'
                            ? 'It may have been moved or unpublished.'
                            : 'Please try again in a moment.'}
                    </p>
                    <button type="button" className="btn-acc" onClick={() => navigate('/dashboard/help')}>
                        Back to Help center
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="rs-help-crumbs">
                <button type="button" onClick={() => navigate('/dashboard/help')}>Help center</button>
                {article.category && (
                    <>
                        <span className="sep">/</span>
                        <span className="here">{article.category.name}</span>
                    </>
                )}
            </div>

            <div className="rs-help-layout">
                <div className="rs-help-main">
                    <article className="rs-card rs-help-article">
                        <h1 className="rs-help-title">{article.title}</h1>
                        {article.summary && <p className="rs-help-summary">{article.summary}</p>}
                        {article.updatedAt && (
                            <div className="rs-help-meta">Last updated {formatDate(article.updatedAt)}</div>
                        )}
                        {article.heroImageUrl && (
                            <figure className="rs-help-hero">
                                <img src={resolveSupportAssetUrl(article.heroImageUrl)} alt="" loading="lazy" />
                            </figure>
                        )}
                        <div className="rs-help-body">
                            {(article.content || []).map((node, i) => (
                                <Node key={i} node={node} />
                            ))}
                        </div>

                        <div className="rs-help-feedback">
                            {feedback ? (
                                <span className="thanks">Thanks for the feedback!</span>
                            ) : (
                                <>
                                    <span>Was this article helpful?</span>
                                    <button type="button" onClick={() => setFeedback('yes')}>Yes</button>
                                    <button type="button" onClick={() => setFeedback('no')}>Not really</button>
                                </>
                            )}
                        </div>
                    </article>

                    {(prev || next) && (
                        <div className="rs-help-pager">
                            {prev ? (
                                <button type="button" onClick={() => navigate(`/dashboard/help/${prev.slug}`)}>
                                    <span className="dir">← Previous</span>
                                    <span className="label">{prev.title}</span>
                                </button>
                            ) : <span />}
                            {next ? (
                                <button type="button" className="next" onClick={() => navigate(`/dashboard/help/${next.slug}`)}>
                                    <span className="dir">Next →</span>
                                    <span className="label">{next.title}</span>
                                </button>
                            ) : <span />}
                        </div>
                    )}
                </div>

                <aside className="rs-help-side">
                    {toc.length > 1 && (
                        <div className="rs-card">
                            <div className="rs-card-h"><h3>In this article</h3></div>
                            <div className="rs-help-toc">
                                {toc.map((h) => (
                                    <a key={h.id} href={`#${h.id}`}>{h.text}</a>
                                ))}
                            </div>
                        </div>
                    )}

                    {siblings.length > 1 && (
                        <div className="rs-card">
                            <div className="rs-card-h">
                                <h3>More in {article.category?.name}</h3>
                            </div>
                            <div className="rs-help-related">
                                {siblings.map((a) => (
                                    <button
                                        key={a.slug}
                                        type="button"
                                        className={a.slug === articleSlug ? 'on' : ''}
                                        onClick={() => a.slug !== articleSlug && navigate(`/dashboard/help/${a.slug}`)}
                                    >
                                        {a.title}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="rs-card rs-help-cta">
                        <b>Still stuck?</b>
                        <p>Our support team usually replies within one business day.</p>
                        <button type="button" className="btn-acc" onClick={() => navigate('/dashboard/support?tab=tickets')}>
                            Contact support
                        </button>
                    </div>
                </aside>
            </div>
        </>
    );
};

export default HelpArticle;
